import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Spinner, InputGroup, FormControl } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as FaIcons from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import PageService from "../services/PageService";
import IconPicker, { getIconComponent } from "./IconPicker";
import { API_BASE_URL } from '../configs/apiConfig';

const PageMgn = () => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(false);
    const [selectedPage, setSelectedPage] = useState({
        name: "",
        code: "",
        iconCode: "",
        parentPageId: null,
        isActive: true,
        orderIndex: 0
    });
    const [expandedParents, setExpandedParents] = useState(new Set());
    const [modalValidated, setModalValidated] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        fetchPages();
    }, []);

const fetchPages = async () => {
    try {
        setLoading(true);
        const data = await PageService.getAll();

        // Ensure child pages remain inside the structure
        const pageMap = {};
        data.forEach(page => {
            pageMap[page.id] = { ...page };
        });

        // Assign children correctly
        data.forEach(page => {
            if (page.parentPageId && pageMap[page.parentPageId]) {
                pageMap[page.parentPageId].children.push(page);
            }
        });

        // Only include top-level pages
        const rootPages = data.filter(page => !page.parentPageId);

        setPages(rootPages);
        
        // Initialize expandedParents with ALL parent page IDs (not just top-level)
        const getAllParentIds = (pagesList) => {
            let parentIds = new Set();
            
            const collectParentIds = (pages) => {
                pages.forEach(page => {
                    if (page.children && page.children.length > 0) {
                        parentIds.add(page.id);
                        collectParentIds(page.children);
                    }
                });
            };
            
            collectParentIds(pagesList);
            return parentIds;
        };
        
        setExpandedParents(getAllParentIds(rootPages));
        
    } catch (error) {
        setError("Failed to fetch pages.");
        toast.error("Error fetching pages.");
    } finally {
        setLoading(false);
    }
};

    const handleDelete = async (id) => {
        // Find the page and check if it has children
        const findPageById = (id, pagesList) => {
            for (const page of pagesList) {
                if (page.id === id) return page;
                if (page.children && page.children.length > 0) {
                    const found = findPageById(id, page.children);
                    if (found) return found;
                }
            }
            return null;
        };

        const pageToDelete = findPageById(id, pages);
        if (!pageToDelete) {
            toast.error("Page not found.");
            return;
        }

        const hasChildren = pageToDelete.children && pageToDelete.children.length > 0;
        const message = hasChildren
            ? `Warning: This page has ${pageToDelete.children.length} child page(s). Deleting this page will also delete all its child pages. Are you sure you want to continue?`
            : "Are you sure you want to delete this page?";

        if (!window.confirm(message)) return;

        try {
            await PageService.delete(id);
            await fetchPages();
            toast.success("Page deleted successfully!");
        } catch (error) {
            // Only show generic error message since we're handling child pages
            toast.error("Failed to delete page. Please try again.");
        }
    };

    const handleSave = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        
        if (!form.checkValidity()) {
            event.stopPropagation();
            setModalValidated(true);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('name', selectedPage.name);
            formData.append('code', selectedPage.code);
            formData.append('iconCode', selectedPage.iconCode);
            formData.append('parentPageId', selectedPage.parentPageId || '');
            formData.append('isActive', selectedPage.isActive);
            formData.append('orderIndex', selectedPage.orderIndex);
            
            if (selectedImage) {
                formData.append('imageFile', selectedImage);
            }

            if (editing) {
                await PageService.update(selectedPage.id, formData);
                toast.success("Page updated successfully!");
            } else {
                await PageService.add(formData);
                toast.success("Page added successfully!");
            }
            setShowModal(false);
            setModalValidated(false);
            setSelectedImage(null);
            setImagePreview(null);
            await fetchPages();
        } catch (error) {
            toast.error(error.message || "Failed to save page.");
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }

    const handleEdit = (page) => {
        setSelectedPage({ ...page });
        setEditing(true);
        setShowModal(true);
        if (page.iconImage) {
            setImagePreview(`${API_BASE_URL}${page.iconImage}`);
            // Create a dummy file object to represent the existing image
            const dummyFile = new File([], page.iconImage.split('/').pop(), { type: 'image/*' });
            setSelectedImage(dummyFile);
        } else {
            setImagePreview(null);
            setSelectedImage(null);
        }
    };

    const handleAddNew = () => {
        setSelectedPage({
            name: "",
            code: "",
            iconCode: "",
            parentPageId: null,
            isActive: true,
            orderIndex: pages.length
        });
        setEditing(false);
        setShowModal(true);
        setModalValidated(false);
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleAddChild = (parentId) => {
        // Find the parent page to get its details
        const findPageById = (id, pagesList) => {
            for (const page of pagesList) {
                if (page.id === id) return page;
                if (page.children && page.children.length > 0) {
                    const found = findPageById(id, page.children);
                    if (found) return found;
                }
            }
            return null;
        };
        
        const parentPage = findPageById(parentId, pages);
        const siblingPages = parentPage?.children || [];
        
        setSelectedPage({
            name: "",
            code: "",
            iconCode: "",
            parentPageId: parentId,
            isActive: true,
            orderIndex: siblingPages.length
        });
        setEditing(false);
        setShowModal(true);
        setModalValidated(false);
    };

    const toggleExpand = (id) => {
        setExpandedParents(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const onDragEnd = async (result) => {
        if (!result.destination) return;

        try {
            const { source, destination, draggableId, type } = result;
            
            // Extract the parent ID from the droppableId (format: "parent-{id}" or "root")
            const sourceParentId = source.droppableId === "root" ? null : 
                parseInt(source.droppableId.replace("parent-", ""));
            const destParentId = destination.droppableId === "root" ? null : 
                parseInt(destination.droppableId.replace("parent-", ""));
            
            // If source and destination parents are different, we don't support that yet
            if (sourceParentId !== destParentId) {
                toast.warning("Moving pages between different parents is not supported yet.");
                return;
            }
            
            // Find the correct list of pages to reorder
            let itemsToReorder;
            if (sourceParentId === null) {
                // Reordering top-level pages
                itemsToReorder = Array.from(pages);
            } else {
                // Reordering child pages - find the parent page first
                const findParentPage = (id, pagesList) => {
                    for (const page of pagesList) {
                        if (page.id === id) return page;
                        if (page.children && page.children.length > 0) {
                            const found = findParentPage(id, page.children);
                            if (found) return found;
                        }
                    }
                    return null;
                };
                
                const parentPage = findParentPage(sourceParentId, pages);
                if (!parentPage || !parentPage.children) {
                    toast.error("Could not find parent page.");
                    return;
                }
                itemsToReorder = Array.from(parentPage.children);
            }
            
            // Perform the reordering
            const [reorderedItem] = itemsToReorder.splice(source.index, 1);
            itemsToReorder.splice(destination.index, 0, reorderedItem);
            
            // Update order indices
            const updatedItems = itemsToReorder.map((item, index) => ({
                ...item,
                orderIndex: index
            }));
            
            // Update the state
            if (sourceParentId === null) {
                setPages(updatedItems);
            } else {
                // Create a deep copy of pages and update the children of the parent
                const updateChildrenInPages = (parentId, newChildren, pagesList) => {
                    return pagesList.map(page => {
                        if (page.id === parentId) {
                            return { ...page, children: newChildren };
                        }
                        if (page.children && page.children.length > 0) {
                            return {
                                ...page,
                                children: updateChildrenInPages(parentId, newChildren, page.children)
                            };
                        }
                        return page;
                    });
                };
                
                setPages(updateChildrenInPages(sourceParentId, updatedItems, pages));
            }
            
            // Get the ordered IDs for the API call
            const orderedIds = updatedItems.map(item => item.id);
            
            // Call the API to update the order in the backend
            await PageService.reorderPages(sourceParentId, orderedIds);
            
            toast.success("Pages reordered successfully!");
        } catch (error) {
            toast.error("Failed to reorder pages: " + (error.message || "Unknown error"));
            // Refresh to get the current order
            await fetchPages();
        }
    };

    const renderPages = (pagesList, level = 0, parentId = null) => {
        return pagesList.map((page, index) => (
            <React.Fragment key={page.id || `item-${index}`}>
                {/* Parent Page Row */}
                <Draggable 
                    draggableId={`page-${page.id}`} 
                    index={index}
                    type={parentId === null ? "root" : `parent-${parentId}`}
                >
                    {(provided) => (
                        <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <td><FaIcons.FaGripVertical /></td>
    
                            {/* Name with Expand/Collapse Button */}
                            <td style={{ paddingLeft: `${level * 20}px` }}>
                                {/* Expand Button or Spacer for Consistency */}
                                {page.children && page.children.length > 0 ? (
                                    <Button 
                                        variant="link" 
                                        className="p-0 me-2" 
                                        onClick={() => toggleExpand(page.id)}
                                    >
                                        {expandedParents.has(page.id) ? <FaIcons.FaChevronDown /> : <FaIcons.FaChevronRight />}
                                    </Button>
                                ) : (
                                    <span className="d-inline-block me-2" style={{ width: "16px" }}></span>
                                )}
                                {page.name}
                            </td>
    
                            <td>{page.code}</td>
                            <td>
                                <div className="d-flex align-items-center">
                                    {getIconComponent(page.iconCode)}
                                </div>
                            </td>
                            <td>
                                {page.iconImage && (
                                    <img 
                                        src={page.iconImage.startsWith('http') ? page.iconImage : `${API_BASE_URL}${page.iconImage}`}
                                        alt="Page icon"
                                        style={{ 
                                            width: '24px', 
                                            height: '24px', 
                                            objectFit: 'cover'
                                        }}
                                    />
                                )}
                            </td>
                            <td>{page.parentPageId ? 
                                (() => {
                                    // Find parent page name
                                    const findParentName = (id, pagesList) => {
                                        for (const p of pagesList) {
                                            if (p.id === id) return p.name;
                                            if (p.children && p.children.length > 0) {
                                                const found = findParentName(id, p.children);
                                                if (found) return found;
                                            }
                                        }
                                        return "Unknown";
                                    };
                                    return findParentName(page.parentPageId, pages);
                                })() 
                                : "None"}
                            </td>
    
                            {/* Actions */}
                            <td>
                                <Button variant="success" size="sm" className="me-1" onClick={() => handleAddChild(page.id)}>
                                    <FaIcons.FaPlus />
                                </Button>
                                <Button variant="warning" size="sm" className="me-1" onClick={() => handleEdit(page)}>
                                    <FaIcons.FaEdit />
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDelete(page.id)}>
                                    <FaIcons.FaTrash />
                                </Button>
                            </td>
                        </tr>
                    )}
                </Draggable>
    
                {/* Recursively Render Children When Expanded */}
                {expandedParents.has(page.id) && page.children && page.children.length > 0 && (
                    <tr>
                        <td colSpan="6" className="p-0 border-0">
                            <Droppable droppableId={`parent-${page.id}`} type={`parent-${page.id}`}>
                                {(provided) => (
                                    <div 
                                        ref={provided.innerRef} 
                                        {...provided.droppableProps}
                                        className="w-100"
                                    >
                                        <Table bordered hover className="mb-0 w-100">
                                            <colgroup>
                                                <col style={{ width: "50px" }} />
                                                <col style={{ width: "25%" }} />
                                                <col style={{ width: "20%" }} />
                                                <col style={{ width: "10%" }} />
                                                <col style={{ width: "15%" }} />
                                                <col style={{ width: "280px" }} />
                                            </colgroup>
                                            <tbody>
                                                {renderPages(page.children, level + 1, page.id)}
                                                {provided.placeholder}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Droppable>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        ));
    };
    

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Button variant="primary" onClick={handleAddNew}>
                    <FaIcons.FaPlus /> Add New Page
                </Button>
                <InputGroup style={{ maxWidth: "300px" }}>
                    <FormControl
                        placeholder="Search pages..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </InputGroup>
            </div>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="root" type="root">
                        {(provided) => (
                            <Table striped bordered hover responsive ref={provided.innerRef} {...provided.droppableProps}>
                                <colgroup>
                                    <col style={{ width: "50px" }} />
                                    <col style={{ width: "25%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "15%" }} />
                                    <col style={{ width: "280px" }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={{ width: "50px" }}></th>
                                        <th>Name</th>
                                        <th>Code</th>
                                        <th>Icon</th>
                                        <th>Image</th>
                                        <th>Parent</th>
                                        <th style={{ width: "280px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pages.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center">No pages found.</td>
                                        </tr>
                                    ) : (
                                        renderPages(pages)
                                    )}
                                    {provided.placeholder}
                                </tbody>
                            </Table>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            {/* Add/Edit Page Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Form noValidate validated={modalValidated} onSubmit={handleSave}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editing ? "Edit Page" : "Add New Page"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter page name"
                                value={selectedPage.name}
                                onChange={(e) => setSelectedPage({ ...selectedPage, name: e.target.value })}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide a page name.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Code</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter page code"
                                value={selectedPage.code}
                                onChange={(e) => setSelectedPage({ ...selectedPage, code: e.target.value })}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide a page code.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Icon</Form.Label>
                            <IconPicker
                                selectedIcon={selectedPage.iconCode}
                                onIconSelect={(iconCode) => setSelectedPage({ ...selectedPage, iconCode })}
                                isInvalid={modalValidated && !selectedPage.iconCode}
                            />
                            <div className="mt-2">
                                <Form.Label className="small text-muted">Page Icon Image</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    size="sm"
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Page preview" 
                                            style={{ 
                                                maxWidth: '100px', 
                                                maxHeight: '100px',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Parent Page</Form.Label>
                            <Form.Select
                                value={selectedPage.parentPageId || ""}
                                onChange={(e) => setSelectedPage({ 
                                    ...selectedPage, 
                                    parentPageId: e.target.value ? Number(e.target.value) : null 
                                })}
                            >
                                <option value="">None</option>
                                {(() => {
                                    // Helper function to flatten the page hierarchy
                                    const getAllPages = (pagesList, result = []) => {
                                        pagesList.forEach(page => {
                                            result.push(page);
                                            if (page.children && page.children.length > 0) {
                                                getAllPages(page.children, result);
                                            }
                                        });
                                        return result;
                                    };
                                    
                                    // Get all pages in a flat array
                                    const allPages = getAllPages(pages);
                                    
                                    // Filter out the current page to prevent self-reference
                                    return allPages
                                        .filter(page => page.id !== selectedPage.id)
                                        .map((page) => (
                                            <option key={page.id} value={page.id}>
                                                {page.name}
                                            </option>
                                        ));
                                })()}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Active"
                                checked={selectedPage.isActive}
                                onChange={(e) => setSelectedPage({ 
                                    ...selectedPage, 
                                    isActive: e.target.checked 
                                })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {editing ? "Update" : "Add"} Page
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default PageMgn;